import { z } from 'zod';
/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 V108, V111, V133, V139): "Problems, goals, and objectives
 * addressed", its own section on the progress note. Every problem, goal and objective on the
 * client's linked plan is a chip (none on by default); "+ Add" adds the clinician's own item; the
 * X on a picked chip asks This note only / This client going forward / All clients going forward.
 * Sign-off needs at least one picked when the practice asks for it (default on).
 *
 * Pure, so the editor, the sign-off check (web and API) and the field-change cards read one list.
 */
// ── The practice setting (Note settings > Practice note settings) ─────────────────────────────
/** The key in organizations."clinicalSettings" (GET /practice/clinical). */
export const REQUIRE_ADDRESSED_FOCUS_KEY = 'requireAddressedFocus';
/** V133: on unless an admin turns it off (payers ask for it). */
export const REQUIRE_ADDRESSED_FOCUS_DEFAULT = true;
/** The practice setting from the stored (or fetched) clinical settings; not set reads as on. */
export function requireAddressedFocusOf(settings) {
    const v = settings && typeof settings === 'object'
        ? settings[REQUIRE_ADDRESSED_FOCUS_KEY]
        : undefined;
    return typeof v === 'boolean' ? v : REQUIRE_ADDRESSED_FOCUS_DEFAULT;
}
/** V141's What's left item. */
export const FOCUS_LEFT_LABEL = 'At least one problem, goal, or objective addressed';
/** The section's name (and its block id). */
export const FOCUS_LABEL = 'Problems, goals, and objectives addressed';
export const FOCUS_BLOCK_ID = 'focus';
// ── One chip ──────────────────────────────────────────────────────────────────────────────────
export const FOCUS_KINDS = ['problem', 'goal', 'objective', 'custom'];
export const FOCUS_LABEL_MAX = 300;
/** A chip as the note keeps it: the words are copied in, so the note reads the same later. */
export const focusItemSchema = z.object({
    key: z.string().min(1).max(FOCUS_LABEL_MAX),
    label: z.string().trim().min(1).max(FOCUS_LABEL_MAX),
    kind: z.enum(FOCUS_KINDS),
});
/** A chip's key: its words, without case or extra spaces (TP plans carry no ids for goals). */
export const focusKey = (label) => label.replace(/\s+/g, ' ').trim().toLowerCase().slice(0, FOCUS_LABEL_MAX);
const clip = (s) => s.replace(/\s+/g, ' ').trim().slice(0, FOCUS_LABEL_MAX);
const item = (label, kind) => {
    const l = clip(label);
    return l ? { key: focusKey(l), label: l, kind } : null;
};
const s = (v) => (typeof v === 'string' ? v : '');
/**
 * The plan's chips in plan order (V141 planFocusItems): its problems, then each goal followed by
 * its objectives. From the note's goal rows (one per objective; a goal with none is one row) and
 * the plan's problems. TP plans' summary (/tp-summary) has no problems yet: then there are none.
 */
export function planFocusItems(problems, rows) {
    const out = [];
    const push = (it) => {
        if (it && !out.some((o) => o.key === it.key))
            out.push(it);
    };
    for (const p of problems ?? [])
        push(item(p, 'problem'));
    for (const r of rows ?? []) {
        push(item(s(r.goalText), 'goal'));
        push(item(s(r.objectiveText), 'objective'));
    }
    return out;
}
/**
 * The chips picked on a saved note. Notes saved before this section kept "addressed" as a tick on
 * each goal row: those read as the objective (or the goal, on a goal with no objective) picked.
 */
export function addressedFocusOf(body) {
    if (Array.isArray(body.focusAddressed))
        return body.focusAddressed.flatMap((x) => {
            const r = focusItemSchema.safeParse(x);
            return r.success ? [r.data] : [];
        });
    const out = [];
    for (const r of (Array.isArray(body.goalsAddressed)
        ? body.goalsAddressed
        : [])) {
        if (!r || typeof r !== 'object' || r.addressed !== true)
            continue;
        const it = s(r.objectiveText).trim()
            ? item(s(r.objectiveText), 'objective')
            : item(s(r.goalText), 'goal');
        if (it && !out.some((o) => o.key === it.key))
            out.push(it);
    }
    return out;
}
/**
 * The goal rows with "addressed" kept in step with the chips (an objective row: its objective
 * picked; a goal with no objective: the goal picked), so what reads the old tick still reads true.
 */
export function withAddressedRows(rows, on) {
    const keys = new Set(on.map((o) => o.key));
    return rows.map((r) => {
        const obj = s(r.objectiveText).trim();
        const want = keys.has(focusKey(obj || s(r.goalText)));
        return r.addressed === want ? r : { ...r, addressed: want };
    });
}
// ── The clinician's own lists (users."notePrefs".focus) ───────────────────────────────────────
/**
 * Their own items ("+ Add") per client, and what they took off going forward: for one client, or
 * for all their clients (by key, so a goal of the same words on another client's plan goes too).
 */
const keyList = z.array(z.string().min(1).max(FOCUS_LABEL_MAX)).max(200);
const clientMap = (v) => z
    .record(z.string().min(1).max(64), v)
    .refine((r) => Object.keys(r).length <= 500, { message: 'Too many clients.' });
export const focusPrefsSchema = z.object({
    added: clientMap(z.array(z.string().trim().min(1).max(FOCUS_LABEL_MAX)).max(100)).default({}),
    hidden: z
        .object({ all: keyList.default([]), clients: clientMap(keyList).default({}) })
        .default({ all: [], clients: {} }),
});
export const EMPTY_FOCUS_PREFS = { added: {}, hidden: { all: [], clients: {} } };
/** Stored prefs, keeping only what fits; none saved yet reads as empty. */
export function readFocusPrefs(v) {
    const r = focusPrefsSchema.safeParse(v ?? {});
    return r.success ? r.data : { ...EMPTY_FOCUS_PREFS };
}
/** "+ Add": the item is this client's from now on (and back if it was taken off). */
export function focusPrefsWithAdded(prefs, label, contactId) {
    const l = clip(label);
    const k = focusKey(l);
    const mine = prefs.added[contactId] ?? [];
    const added = mine.some((x) => focusKey(x) === k) ? mine : [...mine, l];
    const unhide = (list) => list.filter((x) => x !== k);
    return {
        added: { ...prefs.added, [contactId]: added },
        hidden: {
            all: unhide(prefs.hidden.all),
            clients: {
                ...prefs.hidden.clients,
                ...(prefs.hidden.clients[contactId]
                    ? { [contactId]: unhide(prefs.hidden.clients[contactId]) }
                    : {}),
            },
        },
    };
}
/**
 * The Remove question's two forward scopes: This client going forward takes the item off this
 * client's new notes; All clients going forward off every client's. Saved and signed notes never
 * change. Null when there is nothing new to save.
 */
export function focusPrefsWithout(prefs, key, scope, contactId) {
    const drop = (l) => l.filter((x) => focusKey(x) !== key);
    if (scope === 'client') {
        const hid = prefs.hidden.clients[contactId] ?? [];
        if (hid.includes(key) && !(prefs.added[contactId] ?? []).some((x) => focusKey(x) === key))
            return null;
        return {
            added: prefs.added[contactId]
                ? { ...prefs.added, [contactId]: drop(prefs.added[contactId]) }
                : prefs.added,
            hidden: {
                ...prefs.hidden,
                clients: {
                    ...prefs.hidden.clients,
                    [contactId]: hid.includes(key) ? hid : [...hid, key],
                },
            },
        };
    }
    const inAdded = Object.values(prefs.added).some((l) => l.some((x) => focusKey(x) === key));
    if (prefs.hidden.all.includes(key) && !inAdded)
        return null;
    return {
        added: Object.fromEntries(Object.entries(prefs.added).map(([c, l]) => [c, drop(l)])),
        hidden: {
            ...prefs.hidden,
            all: prefs.hidden.all.includes(key) ? prefs.hidden.all : [...prefs.hidden.all, key],
        },
    };
}
/**
 * The chips the section shows, in order: the plan's, then this client's own items, then anything
 * picked on this note that is in neither (a custom item, or a plan row since changed). A chip taken
 * off this note (off) or going forward (prefs) is left out unless it is picked.
 */
export function focusChoices(o) {
    const onKeys = new Set(o.on.map((x) => x.key));
    const hidden = new Set([...o.prefs.hidden.all, ...(o.prefs.hidden.clients[o.contactId] ?? [])]);
    const off = new Set(o.off ?? []);
    const own = (o.prefs.added[o.contactId] ?? []).flatMap((l) => {
        const it = item(l, 'custom');
        return it ? [it] : [];
    });
    const out = [];
    for (const it of [...o.plan, ...own, ...o.on]) {
        if (out.some((x) => x.key === it.key))
            continue;
        const on = onKeys.has(it.key);
        if (!on && (hidden.has(it.key) || off.has(it.key)))
            continue;
        out.push({ ...it, on });
    }
    return out;
}
//# sourceMappingURL=noteFocus.js.map